import { TITLE_METADATA_KEY } from "@/domain/case/case-config";

/**
 * `metadata_extraction_paths` の 1 件。`key` は一覧・詳細で使う名前、
 * `path` は起票データ内の場所（今の画面はフラットな項目だけを扱うので同じ値）。
 */
export type MetadataPath = {
  readonly key: string;
  readonly path: string;
};

/**
 * 画面で選んだ項目を宣言に反映する。
 *
 * 宣言していない項目は、入力されていても案件一覧・詳細に出ない（backend が
 * metadata として抽出しないため）。フォームに項目を足しただけでは見えない、という
 * 二重管理をここで解消する。
 *
 * 触るのは**フォーム定義に存在する項目だけ**。フォームに無い宣言（スクリプトが書く
 * 判定など）は選択の対象にできないので、そのまま残す（消すと差し戻し判定が壊れる）。
 * 件名の予約キーは一覧と検索の土台なので、選択に関わらず必ず残す。
 */
export function mergeMetadataPaths(
  current: readonly MetadataPath[],
  selectedFields: readonly string[],
  formFields: readonly string[],
): readonly MetadataPath[] {
  const inForm = new Set(formFields);
  const selected = new Set(selectedFields);

  const kept = current.filter(
    (entry) => !inForm.has(entry.key) && entry.key !== TITLE_METADATA_KEY,
  );

  const title =
    current.find((entry) => entry.key === TITLE_METADATA_KEY) ??
    ({ key: TITLE_METADATA_KEY, path: TITLE_METADATA_KEY } as MetadataPath);

  const added = [...selected]
    .filter((field) => field !== TITLE_METADATA_KEY)
    .map((field) => ({
      // path は現在の宣言を尊重する（入れ子を指している場合を壊さない）
      key: field,
      path: current.find((entry) => entry.key === field)?.path ?? field,
    }));

  return [title, ...kept, ...added];
}

/** 宣言済みのフォーム項目（チェックボックスの初期状態）。 */
export function declaredFormFields(
  current: readonly MetadataPath[],
  formFields: readonly string[],
): readonly string[] {
  const inForm = new Set(formFields);

  return current
    .filter((entry) => inForm.has(entry.key))
    .map((entry) => entry.key);
}
